import React from 'react';
import { motion } from 'framer-motion';
import { ArrowLeft, Eye, Users, CheckCircle, Heart, Euro, FileText, Mail } from 'lucide-react';
import { Link, useNavigate } from 'react-router-dom';

const TransparencyEN = () => {
  const navigate = useNavigate();

  const handleDonateClick = () => {
    navigate('/en#donate');
    setTimeout(() => {
      const element = document.getElementById('donate');
      if (element) {
        element.scrollIntoView({ behavior: 'smooth', block: 'start' });
      }
    }, 100);
  };
  const transparencyItems = [
    {
      icon: Users,
      title: "Who we are",
      content: "hay - humanity. action. you. e.V. is a non-profit organization from Bochum, initiated by four siblings with Palestinian-German backgrounds: Stefan, Thomas, Daniel, and Nadja. Together with their parents Abdul and Brigitte and their longtime friend Tim, the association was founded in 2025. Our goal: Humanitarian aid for people in need – with a focus on acute crises like in the Gaza Strip."
    },
    {
      icon: CheckCircle,
      title: "Charitable Status",
      content: "We are currently in the process of obtaining charitable status recognition. Donations can be claimed for tax purposes after successful recognition (not retroactively). Once charitable status is confirmed, we will be happy to issue corresponding donation certificates. We are happy to answer questions about this at: info@hayforhumanity.de"
    },
    {
      icon: Heart,
      title: "Funding",
      content: "Our aid is based exclusively on donations. We waive administrative costs – everything that is donated goes directly to aid on the ground."
    },
    {
      icon: Euro,
      title: "Use of Donations",
      content: "Every donation is documented. We work with trusted contacts in Gaza and Egypt."
    }
  ];

  return (
    <div className="min-h-screen bg-warm-beige pt-32 pb-20">
      <div className="container mx-auto px-4 sm:px-6 lg:px-8">
        <div className="max-w-4xl mx-auto">
          {/* Back Button */}
          <motion.div
            className="mb-8"
            initial={{ opacity: 0, x: -30 }}
            animate={{ opacity: 1, x: 0 }}
            transition={{ duration: 0.6 }}
          >
            <Link
              to="/en"
              className="inline-flex items-center space-x-2 text-olive-green hover:text-olive-dark transition-colors font-medium"
            >
              <ArrowLeft className="w-5 h-5" />
              <span>Back to Homepage</span>
            </Link>
          </motion.div>

          {/* Header */}
          <motion.div
            className="text-center mb-16"
            initial={{ opacity: 0, y: 30 }}
            animate={{ opacity: 1, y: 0 }}
            transition={{ duration: 0.8 }}
          >
            <div className="mb-6">
              <h1 className="text-3xl sm:text-4xl font-bold text-charcoal font-heading text-center">
                Our Transparency.
              </h1>
            </div>
            <p className="text-lg sm:text-xl text-charcoal/80 max-w-2xl mx-auto leading-relaxed">
              For your trust.
            </p>
            <div className="w-24 h-1 bg-olive-green mx-auto mt-6"></div>
          </motion.div>

          {/* Main Content */}
          <motion.div
            className="bg-white rounded-2xl shadow-xl p-6 sm:p-8 md:p-12 border border-olive-green/10 mb-8"
            initial={{ opacity: 0, y: 30 }}
            animate={{ opacity: 1, y: 0 }}
            transition={{ duration: 0.8, delay: 0.2 }}
          >
            {/* Transparency Items */}
            <div className="space-y-8 sm:space-y-12">
              {transparencyItems.map((item, index) => (
                <motion.div
                  key={item.title}
                  className="flex items-start space-x-4 sm:space-x-6"
                  initial={{ opacity: 0, x: -30 }}
                  animate={{ opacity: 1, x: 0 }}
                  transition={{ duration: 0.6, delay: 0.3 + index * 0.1 }}
                >
                  <div className="w-12 h-12 sm:w-16 sm:h-16 bg-olive-green/10 rounded-full flex items-center justify-center flex-shrink-0">
                    <item.icon className="w-6 h-6 sm:w-8 sm:h-8 text-olive-green" />
                  </div>
                  <div className="flex-1">
                    <h2 className="text-xl sm:text-2xl font-bold text-charcoal mb-3 sm:mb-4 font-heading">
                      {index + 1}. {item.title}
                    </h2>
                    <p className="text-charcoal/80 leading-relaxed text-base sm:text-lg">
                      {item.content}
                    </p>
                  </div>
                </motion.div>
              ))}

            </div>
          </motion.div>

          {/* Contact Section */}
          <motion.div
            className="bg-gradient-to-r from-olive-green to-olive-dark rounded-2xl p-6 sm:p-8 md:p-12 text-white"
            initial={{ opacity: 0, y: 30 }}
            animate={{ opacity: 1, y: 0 }}
            transition={{ duration: 0.8, delay: 0.8 }}
          >
            <div className="text-center">
              <div className="mb-6">
                <h2 className="text-2xl sm:text-3xl font-bold font-heading text-center">
                  Contact & Exchange
                </h2>
              </div>
              <p className="text-lg sm:text-xl text-white/90 leading-relaxed max-w-2xl mx-auto">
                We are happy to answer questions about the use of donations. Transparency is part of responsibility for us.
              </p>
              <div className="mt-8">
                <a 
                  href="mailto:info@hayforhumanity.de"
                  className="inline-flex items-center space-x-2 bg-white text-olive-green px-6 py-3 sm:px-8 sm:py-4 rounded-full font-bold text-base sm:text-lg transition-all transform hover:scale-105 shadow-xl"
                >
                  <Mail className="w-5 h-5" />
                  <span>info@hayforhumanity.de</span>
                </a>
              </div>
            </div>
          </motion.div>

          {/* Additional Info */}
          <motion.div
            className="mt-8 p-6 bg-white/80 backdrop-blur-sm rounded-xl border border-olive-green/20"
            initial={{ opacity: 0, y: 20 }}
            animate={{ opacity: 1, y: 0 }}
            transition={{ duration: 0.6, delay: 1 }}
          >
            <div className="flex items-start space-x-3">
              <FileText className="w-6 h-6 text-olive-green flex-shrink-0 mt-1" />
              <p className="text-charcoal/70 leading-relaxed">
                This transparency statement is updated regularly. Changes to our association structure or 
                status will be communicated here promptly.
              </p>
            </div>
          </motion.div>

          {/* Donate CTA */}
          <motion.div
            className="mt-8 bg-gradient-to-r from-olive-green to-olive-dark rounded-2xl p-8 text-white text-center"
            initial={{ opacity: 0, y: 30 }}
            animate={{ opacity: 1, y: 0 }}
            transition={{ duration: 0.8, delay: 1.2 }}
          >
            <Heart className="w-12 h-12 mx-auto mb-4" />
            <h3 className="text-2xl font-bold mb-4 font-heading">
              Support our mission.
            </h3>
            <p className="text-lg text-white/90 mb-6 leading-relaxed max-w-2xl mx-auto">
              Every donation helps us support people in need.
            </p>
            <button
              onClick={handleDonateClick}
              className="bg-white text-olive-green hover:bg-cream px-8 py-4 rounded-full font-bold text-lg transition-all transform hover:scale-105 shadow-xl"
            >
              Donate now
            </button>
          </motion.div>
        </div>
      </div>
    </div>
  );
};

export default TransparencyEN;